import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  LinearProgress,
  Stack,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ScriptableContext,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useMemo, useRef } from 'react';
import {
  Send,
  Email,
  HowToReg,
  Twitter,
  Telegram,
  PersonAdd,
  Group,
  People,
  Undo,
  FilterAlt,
} from '@mui/icons-material';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AnalyticsLineChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  historyData?: any[]; // Array of historical analytics entries
  period?: string;
  groupBy?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  onPeriodChange?: (period: string) => void;
  onStartDateChange?: (date: Dayjs | null) => void;
  onEndDateChange?: (date: Dayjs | null) => void;
  onApplyFilters?: () => void;
  onGroupByChange?: (groupBy: string) => void;
  loading?: boolean;
  selectedMetric: string; // The currently selected metric to display
  onMetricChange: (metric: string) => void;
  // User filter props
  isSuperAdmin?: boolean;
  users?: any[];
  selectedUser?: string;
  onUserChange?: (event: any) => void;
}

export default function AnalyticsLineChart({
  historyData = [],
  period = '7',
  groupBy = 'day',
  startDate = null,
  endDate = null,
  onPeriodChange,
  onGroupByChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  selectedMetric,
  onMetricChange,
  isSuperAdmin = false,
  users = [],
  selectedUser = 'all',
  onUserChange,
  loading = false,
}: Omit<AnalyticsLineChartProps, 'data'>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartRef = useRef<any>(null);

  // Map metric keys to readable labels, colors and icons
  const metricConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    connectionsSent: {
      label: 'Connections Sent',
      color: '#10B981',
      icon: <Send sx={{ fontSize: '1.2rem' }} />,
    },
    emailsSent: {
      label: 'Emails Sent',
      color: '#6366F1',
      icon: <Email sx={{ fontSize: '1.2rem' }} />,
    },
    connectionsAccepted: {
      label: 'Connections Accepted',
      color: '#8B5CF6',
      icon: <HowToReg sx={{ fontSize: '1.2rem' }} />,
    },
    twitterDMsSent: {
      label: 'Twitter DMs',
      color: '#1DA1F2',
      icon: <Twitter sx={{ fontSize: '1.2rem' }} />,
    },
    telegramDMsSent: {
      label: 'Telegram DMs',
      color: '#0088cc',
      icon: <Telegram sx={{ fontSize: '1.2rem' }} />,
    },
    leadsGenerated: {
      label: 'Leads Generated',
      color: '#F59E0B',
      icon: <PersonAdd sx={{ fontSize: '1.2rem' }} />,
    },
    existingConnections: {
      label: 'Existing Connections',
      color: '#EC4899',
      icon: <Group sx={{ fontSize: '1.2rem' }} />,
    },
    invertedConnections: {
      label: 'Inverted Connections',
      color: '#06d6a0',
      icon: <Undo sx={{ fontSize: '1.2rem' }} />,
    },
  };

  const currentConfig = metricConfig[selectedMetric] || metricConfig['connectionsSent'];

  // Prepare Chart.js data
  const chartJsData = useMemo(() => {
    const sortedData = [...historyData].sort((a, b) => {
      const dateA = new Date(a.date || a._id || 0).getTime();
      const dateB = new Date(b.date || b._id || 0).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map((entry: any) => {
      const dateVal = entry.date || entry._id || entry.period;
      const date = new Date(dateVal);

      if (groupBy === 'month') {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }

      if (groupBy === 'week') {
        return `W/C ${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;
      }

      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      return `${day} ${month}`;
    });

    const dataPoints = sortedData.map((entry: any) => entry[selectedMetric] || 0);

    return {
      labels,
      datasets: [
        {
          label: currentConfig.label,
          data: dataPoints,
          borderColor: currentConfig.color,
          backgroundColor: (context: ScriptableContext<'line'>) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, alpha(currentConfig.color, 0.25));
            gradient.addColorStop(1, alpha(currentConfig.color, 0.0));
            return gradient;
          },
          borderWidth: 3,
          pointBackgroundColor: currentConfig.color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [historyData, selectedMetric, currentConfig]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 16, 26, 0.95)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: alpha('#fff', 0.1),
        borderWidth: 1,
        padding: 14,
        cornerRadius: 12,
        usePointStyle: true,
        titleFont: { family: 'Lato, sans-serif', size: 13, weight: 'bold' },
        bodyFont: { family: 'Lato, sans-serif', size: 14 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: 'rgba(255, 255, 255, 0.4)',
          font: { size: 11, family: 'Lato, sans-serif' },
        },
      },
      y: {
        position: 'right',
        grid: { display: false },
        ticks: {
          color: 'rgba(255, 255, 255, 0.4)',
          font: { size: 11, family: 'Lato, sans-serif' },
          callback: (val: any) =>
            Number(val) >= 1000 ? `${(Number(val) / 1000).toFixed(1)}K` : val,
        },
      },
    },
  };

  const durationOptions = [
    { label: '7D', value: '7' },
    { label: '1M', value: '30' },
    { label: '3M', value: '90' },
    { label: '1Y', value: '365' },
    { label: 'MAX', value: 'all' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
      {/* Sleek Top Progress Bar */}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: -2,
            left: 0,
            right: 0,
            height: 2,
            zIndex: 10,
            background: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, transparent, ${currentConfig.color}, transparent)`,
            },
          }}
        />
      )}

      {/* Metric Pills - Legend/Selector */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 4,
          px: 1,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {Object.entries(metricConfig).map(([key, config]) => {
          const isSelected = selectedMetric === key;
          return (
            <Box
              key={key}
              onClick={() => onMetricChange(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                px: 2,
                py: 1,
                borderRadius: '8px',
                cursor: 'pointer',
                background: isSelected ? alpha(config.color, 0.1) : 'rgba(255, 255, 255, 0.03)',
                border: `1px solid ${isSelected ? alpha(config.color, 0.4) : 'rgba(255, 255, 255, 0.08)'}`,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: isSelected ? alpha(config.color, 0.15) : 'rgba(255, 255, 255, 0.06)',
                  borderColor: alpha(config.color, 0.4),
                },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: config.color,
                  boxShadow: isSelected ? `0 0 10px ${config.color}` : 'none',
                  transition: 'all 0.3s ease',
                }}
              />
              <Typography
                sx={{
                  color: isSelected ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  fontFamily: 'Lato, sans-serif',
                }}
              >
                {config.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Main Chart Area */}
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: 550,
          background: 'rgba(255, 255, 255, 0.01)',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.04)',
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
            opacity: loading && historyData.length > 0 ? 0.6 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: currentConfig.color,
                boxShadow: `0 0 15px ${alpha(currentConfig.color, 0.6)}`,
              }}
            />
            <Typography
              variant='h6'
              sx={{
                color: '#fff',
                fontWeight: 800,
                letterSpacing: '0.5px',
                fontFamily: 'Lato, sans-serif',
              }}
            >
              Activity Trend
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {isSuperAdmin && (
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <Select
                  value={selectedUser}
                  onChange={onUserChange}
                  disabled={loading}
                  startAdornment={
                    <People sx={{ color: '#fff', fontSize: '1rem', mr: 1, opacity: 0.6 }} />
                  }
                  sx={{
                    height: 38,
                    background: 'rgba(255, 255, 255, 0.03)',
                    color: '#fff',
                    borderRadius: '100px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    fontFamily: 'Lato, sans-serif',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '& .MuiSelect-select': {
                      py: 0,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: '#15161a',
                        color: '#fff',
                        borderRadius: '12px',
                        mt: 1,
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.8)',
                      },
                    },
                  }}
                >
                  <MenuItem value='all' sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    All Users
                  </MenuItem>
                  {users
                    .filter((u) => u.username !== 'superadmin' && u.username !== 'kumar')
                    .map((user) => (
                      <MenuItem
                        key={user._id}
                        value={user.username || user.email}
                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                      >
                        {user.username || user.email}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <Box
              sx={{
                display: 'flex',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '100px',
                p: 0.6,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                height: 38,
                alignItems: 'center',
              }}
            >
              {durationOptions.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => onPeriodChange && onPeriodChange(option.value)}
                  disabled={loading}
                  sx={{
                    minWidth: 45,
                    px: 1.5,
                    height: '100%',
                    borderRadius: '100px',
                    color: period === option.value ? '#000' : 'rgba(255, 255, 255, 0.5)',
                    background: period === option.value ? '#fff' : 'transparent',
                    fontWeight: 800,
                    fontSize: '0.65rem',
                    textTransform: 'none',
                    '&:hover': {
                      background: period === option.value ? '#fff' : 'rgba(255, 255, 255, 0.08)',
                      color: period === option.value ? '#000' : '#fff',
                    },
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '100px',
                p: 0.6,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                height: 38,
                alignItems: 'center',
              }}
            >
              {[
                { label: 'Day', value: 'day' },
                { label: 'Week', value: 'week' },
                { label: 'Month', value: 'month' },
              ].map((option) => (
                <Button
                  key={option.value}
                  onClick={() => onGroupByChange && onGroupByChange(option.value)}
                  disabled={loading}
                  sx={{
                    minWidth: 55,
                    px: 1.5,
                    height: '100%',
                    borderRadius: '100px',
                    color: groupBy === option.value ? '#000' : 'rgba(255, 255, 255, 0.5)',
                    background: groupBy === option.value ? '#fff' : 'transparent',
                    fontWeight: 800,
                    fontSize: '0.65rem',
                    textTransform: 'none',
                    '&:hover': {
                      background: groupBy === option.value ? '#fff' : 'rgba(255, 255, 255, 0.08)',
                      color: groupBy === option.value ? '#000' : '#fff',
                    },
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>

            {period === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction='row' spacing={2} alignItems='center'>
                  <DatePicker
                    label='Start Date'
                    value={startDate}
                    onChange={onStartDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& .MuiInputBase-root': {
                            height: 38,
                            borderRadius: '100px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            color: '#fff',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.75rem',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label='End Date'
                    value={endDate}
                    onChange={onEndDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& .MuiInputBase-root': {
                            height: 38,
                            borderRadius: '100px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            color: '#fff',
                            fontSize: '0.75rem',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.75rem',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={onApplyFilters}
                    disabled={loading || !startDate || !endDate}
                    sx={{
                      bgcolor: alpha(currentConfig.color, 0.1),
                      color: currentConfig.color,
                      height: 38,
                      width: 38,
                      '&:hover': {
                        bgcolor: alpha(currentConfig.color, 0.2),
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'rgba(255, 255, 255, 0.02)',
                        color: 'rgba(255, 255, 255, 0.2)',
                      },
                    }}
                  >
                    <FilterAlt fontSize='small' />
                  </IconButton>
                </Stack>
              </LocalizationProvider>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            height: 450,
            width: '100%',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
          }}
        >
          {loading && historyData.length === 0 ? (
            <Box sx={{ width: '100%', height: '100%' }}>
              <Skeleton
                variant='rectangular'
                width='100%'
                height='100%'
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  '&::after': {
                    background: `linear-gradient(90deg, transparent, ${alpha(currentConfig.color, 0.1)}, transparent)`,
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                opacity: loading ? 0.4 : 1,
                filter: loading ? 'blur(1px)' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <Line ref={chartRef} data={chartJsData} options={options} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
